import { injectable, interfaces } from 'inversify';
import { action, IObservableArray, makeObservable, observable, set } from 'mobx';
import { IPagingInfoWithOrder, PaginationInfoDefault } from '../../models/paginationInfo.model';
import { ProjectStatus } from '../projects/projects.model';
import { Plan } from '../user/user.store';
import { DatasetStatus } from './datasetStatus.model';
import { IImageFile } from './imagesUpload.service';
import { IRejectedFile } from './models/IRejectedFile';

export const DefaultPaging = {
  ...PaginationInfoDefault,
  orderBy: 'name',
  orderType: 'asc',
  search: '',
};

const emptyDetails: IDatasetDetails = {
  id: '',
  name: '',
  workspaceId: '',
  description: '',
  createdDate: '',
  lastModified: '',
  imagesCount: 0,
  imagesSize: 0,
  projectsCount: 0,
  status: 'Draft',
  canDownload: false,
  author: '',
  termsOfUse: '',
  createdBy: '',
  createdById: '',
};

const emptyImagePreviewInfo: IDatasetPreviewImage = {
  height: 0,
  width: 0,
  id: '',
  name: '',
  size: 0,
  createdDate: '',
  hasAnnotations: false,
  hasLock: false,
  hasUrl: false,
};

export interface ISelectedImage {
  id: string;
  hasLock: boolean;
  hasAnnotation: boolean;
}

export interface IPreviewImageAttributes {
  channels: number;
  isCached: boolean;
  downloadSpeed?: number;
}

export interface IDatasetDetails {
  id: string;
  workspaceId: string;
  name: string;
  description: string;
  createdDate: string;
  lastModified: string;
  imagesCount: number;
  imagesSize: number;
  projectsCount: number;
  canDownload: boolean;
  status: DatasetStatus;
  author: string;
  termsOfUse: string;
  createdBy: string;
  createdById: string;
}

export interface IDatasetDetailsImage {
  id: string;
  name: string;
  width: number;
  height: number;
  size: number;
  createdDate: string;
  crs?: string;
  hasAnnotations: boolean;
  hasLocks: boolean;
  url: string;
  acceptedAnnotationsCount: number;
  annotationsCount: number;
  hasFileUrl: boolean;
}

export interface IImageUsageInfo {
  hasLock: boolean;
  hasAnnotation: boolean;
}

export interface IDatasetPreviewImage {
  id: string;
  name: string;
  width: number;
  height: number;
  size: number;
  createdDate: string;
  hasAnnotations: boolean;
  hasLock: boolean;
  hasUrl: boolean;
}

export interface IDatasetDetailsProjects {
  id: string;
  name: string;
  status: ProjectStatus;
  description: string;
  assignedToProjectDate: string;
  reviewsDonePercentage: number;
}

export interface IPreviewPaging {
  wasChanged: boolean;
  checkDate: string;
  items: IPreviewPagingItem[];
}

export interface IPreviewPagingItem {
  imageId: string;
  index: number;
  size: number;
}

export interface IDatasetsDetailsStore {
  markAsUploaded(datasetId: string): void;
  markAsUploading(datasetId: string): void;
  incrementProgress(datasetId: string, imageSize: number): void;
  setImagesPaging(imagesPaging: IPagingInfoWithOrder): void;
  setImages(images: IDatasetDetailsImage[], imagesPaging: IPagingInfoWithOrder, imagesCount: number): void;
  setDatasetDetails(details: IDatasetDetails): void;
  setDatasetDetailsProjects(detailsProjects: IDatasetDetailsProjects[], projectsPaging: IPagingInfoWithOrder): void;
  setDatasetDetailsProjectsPaging(projectsPaging: IPagingInfoWithOrder): void;

  activePreloads: IObservableArray<string>;
  readonly details: IDatasetDetails;
  readonly detailsImages: IObservableArray<IDatasetDetailsImage>;
  readonly detailsImagesPaging: IPagingInfoWithOrder;
  readonly detailsProjects: IObservableArray<IDatasetDetailsProjects>;
  readonly detailsProjectsPaging: IPagingInfoWithOrder;
  imagesToUploadFromFile: IObservableArray<IImageFile>;
  imagesRejectedFromFile: IObservableArray<IRejectedFile>;
  willExceedsUploadImageSizeLimit: boolean;
  ownerPlan: Plan;
  imagePreValidationWasSuccessful: boolean;
  selectedImages: IObservableArray<ISelectedImage>;
  isImageCountExceeded: boolean;
  availableImageCount: number;
  imagePreviewInfo: IDatasetPreviewImage;
  imagePreviewUrl: string;
  lowQualityImagePreviewUrl: string;
  imageAttributes: IPreviewImageAttributes;
  isPreviewImageLoading: boolean;
  previewPaging: IPreviewPaging;
}

export namespace IDatasetsDetailsStore {
  export const $: interfaces.ServiceIdentifier<IDatasetsDetailsStore> = Symbol('DATASETS_DETAILS_STORE');
}

@injectable()
export class DatasetsDetailsStore implements IDatasetsDetailsStore {
  activePreloads: IObservableArray<string> = observable.array([]);

  details: IDatasetDetails = emptyDetails;

  detailsImages: IObservableArray<IDatasetDetailsImage> = observable.array([]);

  detailsImagesPaging: IPagingInfoWithOrder = DefaultPaging;

  detailsProjects: IObservableArray<IDatasetDetailsProjects> = observable.array([]);

  imagesToUploadFromFile: IObservableArray<IImageFile> = observable.array([]);

  imagesRejectedFromFile: IObservableArray<IRejectedFile> = observable.array([]);

  willExceedsUploadImageSizeLimit: boolean = false;

  ownerPlan: Plan = Plan.Free;

  imagePreValidationWasSuccessful: boolean = true;

  selectedImages: IObservableArray<ISelectedImage> = observable.array([]);

  isImageCountExceeded: boolean = false;

  availableImageCount: number = 0;

  imagePreviewInfo: IDatasetPreviewImage = emptyImagePreviewInfo;

  imagePreviewUrl: string = '';

  lowQualityImagePreviewUrl: string = '';

  imageAttributes: IPreviewImageAttributes = { channels: 0, downloadSpeed: 0, isCached: false };

  isPreviewImageLoading: boolean = false;

  previewPaging: IPreviewPaging = { checkDate: '', items: [], wasChanged: false };

  detailsProjectsPaging: IPagingInfoWithOrder = DefaultPaging;

  constructor() {
    makeObservable(this, {
      activePreloads: observable,
      details: observable,
      detailsImages: observable,
      detailsImagesPaging: observable,
      detailsProjects: observable,
      imagesToUploadFromFile: observable,
      imagesRejectedFromFile: observable,
      willExceedsUploadImageSizeLimit: observable,
      ownerPlan: observable,
      imagePreValidationWasSuccessful: observable,
      selectedImages: observable,
      isImageCountExceeded: observable,
      availableImageCount: observable,
      imagePreviewInfo: observable,
      imagePreviewUrl: observable,
      lowQualityImagePreviewUrl: observable,
      imageAttributes: observable,
      isPreviewImageLoading: observable,
      previewPaging: observable,
      detailsProjectsPaging: observable,
      markAsUploaded: action.bound,
      markAsUploading: action.bound,
      incrementProgress: action.bound,
      setImagesPaging: action,
      setImages: action,
      setDatasetDetails: action,
      setDatasetDetailsProjects: action,
      setDatasetDetailsProjectsPaging: action,
    });
  }

  markAsUploaded(datasetId: string): void {
    if (datasetId === this.details.id) {
      this.details.status = this.details.status === 'PublishedUploading' ? 'Published' : 'Draft';
    }
  }

  markAsUploading(datasetId: string): void {
    if (datasetId === this.details.id) {
      this.details.status = this.details.status === 'Draft' ? 'Uploading' : 'PublishedUploading';
    }
  }

  incrementProgress(datasetId: string, imageSize: number): void {
    if (datasetId === this.details.id) {
      this.details.imagesCount += 1;
      this.details.imagesSize += imageSize;
    }
  }

  setImagesPaging(imagesPaging: IPagingInfoWithOrder): void {
    set(this.detailsImagesPaging, imagesPaging);
  }

  setImages(images: IDatasetDetailsImage[], imagesPaging: IPagingInfoWithOrder, imagesCount: number): void {
    this.detailsImages.replace(images);
    set(this.detailsImagesPaging, imagesPaging);
    this.details.imagesCount = imagesCount;
  }

  setDatasetDetails(details: IDatasetDetails): void {
    set(this.details, details);
    this.imagesRejectedFromFile.clear();
    this.imagesToUploadFromFile.clear();
    this.willExceedsUploadImageSizeLimit = false;
    this.ownerPlan = Plan.Free;
    this.imagePreValidationWasSuccessful = true;
    this.isImageCountExceeded = false;
    this.availableImageCount = 0;
    set(this.previewPaging, { checkDate: '', items: [], wasChanged: false });
    set(this.imagePreviewInfo, emptyImagePreviewInfo);
    set(this.imageAttributes, { channels: 0, isCached: false, downloadSpeed: 0 });
  }

  setDatasetDetailsProjects(detailsProjects: IDatasetDetailsProjects[], projectsPaging: IPagingInfoWithOrder): void {
    this.detailsProjects.replace(detailsProjects);
    set(this.detailsProjectsPaging, projectsPaging);
  }

  setDatasetDetailsProjectsPaging(projectsPaging: IPagingInfoWithOrder): void {
    set(this.detailsProjectsPaging, projectsPaging);
  }
}
